import { Component, Input, Output, EventEmitter } from '@angular/core';
import { Subject } from 'rxjs';
import { EmailValidationService, EmailValidationResult, UserSearchResult } from '../../../core/services/email-validation.service';
import { environment } from '../../../../environments/environment';

@Component({
    selector: 'app-share-dialog',
    templateUrl: './share-dialog.component.html',
    styleUrls: ['./share-dialog.component.css'],
    standalone: false
})
export class ShareDialogComponent {
  @Input() item: any;
  @Output() shareApplied = new EventEmitter<{
    items: any[], 
    selectedPeople: any[], 
    removedPeople?: string[] // Emails of people that were removed
  }>();
  
  isVisible: boolean = false;
  items: any[] = []; // Always work with array of items
  newUserEmail: string = '';
  newUserName: string = '';
  selectedPeople: any[] = []; // People with isPartial flag for mixed state
  initialPeopleSnapshot: Set<string> = new Set(); // Track initial people for deletion detection
  newlyAddedPeople: Set<string> = new Set(); // Track people added during this dialog session
  
  // Email validation properties
  emailValidationResult: EmailValidationResult | null = null;
  isValidatingEmail: boolean = false;
  private emailValidationSubject$ = new Subject<string>();
  
  // Debouncing timer (similar to home component)
  private typingTimer: any;
  private readonly DEBOUNCE_TIME = 2000; // 2 seconds wait time
  
  // User search properties
  isSearching: boolean = false;
  searchResults: UserSearchResult[] = [];
  showDropdown: boolean = false;
  
  // Feature flag: Skip validation if allow_non_validated_emails is true
  private readonly skipValidation: boolean = environment.allow_non_validated_emails === true;

  constructor(private emailValidationService: EmailValidationService) {}

  // Always accept array of items (single item = array of 1)
  show(items: any[]) {
    this.items = items;
    this.item = this.items[0]; // For backward compatibility
    this.isVisible = true;
    
    // Analyze sharing state across all items (Windows-style)
    this.analyzeMultipleItemsState();
    
    // Take snapshot of initial people (including partial) for deletion tracking
    this.initialPeopleSnapshot = new Set(this.selectedPeople.map(p => p.email));
    
    // Clear newly added tracker
    this.newlyAddedPeople.clear();
    
    // Reset validation state
    clearTimeout(this.typingTimer);
    this.newUserEmail = '';
    this.newUserName = '';
    this.emailValidationResult = null;
    this.isValidatingEmail = false;
    this.isSearching = false;
    this.searchResults = [];
    this.showDropdown = false;
  }
  
  // Analyze the sharing state across multiple items
  analyzeMultipleItemsState() {
    // Simplified: No radio buttons, just build union of all people across all items
    const peopleMap = new Map<string, {email: string, count: number, isPartial: boolean}>();
    
    this.items.forEach(item => {
      const people = item.selectedPeople || [];
      people.forEach((person: any) => {
        if (peopleMap.has(person.email)) {
          peopleMap.get(person.email)!.count++;
        } else {
          peopleMap.set(person.email, {
            email: person.email,
            count: 1,
            isPartial: false
          });
        }
      });
    });
    
    // Mark people as partial if they don't appear in all items
    const totalItems = this.items.length;
    this.selectedPeople = Array.from(peopleMap.values()).map(person => ({
      email: person.email,
      isPartial: person.count < totalItems, // Gray if not in all items
      itemCount: person.count // How many items have this person
    }));
  }

  close() {
    clearTimeout(this.typingTimer);
    this.isVisible = false;
    this.items = [];
    this.newUserEmail = '';
    this.newUserName = '';
    this.selectedPeople = [];
    this.emailValidationResult = null;
    this.isValidatingEmail = false;
    this.isSearching = false;
    this.searchResults = [];
    this.showDropdown = false;
  }
  
  // Get display text for links
  getLinksDisplayText(): string {
    if (this.items.length === 0) return '';
    if (this.items.length === 1) return this.items[0].shortUrl || this.items[0].shortUrlId || '';
    if (this.items.length === 2) return `${this.items[0].shortUrlId}, ${this.items[1].shortUrlId}`;
    if (this.items.length === 3) return `${this.items[0].shortUrlId}, ${this.items[1].shortUrlId}, ${this.items[2].shortUrlId}`;
    return `${this.items[0].shortUrlId}, ${this.items[1].shortUrlId}, and ${this.items.length - 2} more`;
  }

  // Triggered when user types in email/name input
  onEmailInput(input: string) {
    const trimmedInput = input.trim();
    
    // Clear previous timer
    clearTimeout(this.typingTimer);
    
    // Reset states
    this.emailValidationResult = null;
    this.searchResults = [];
    this.showDropdown = false;
    
    // Don't process empty input
    if (!trimmedInput) {
      this.isValidatingEmail = false;
      this.isSearching = false;
      return;
    }
    
    // If validation is disabled, skip all validation logic
    if (this.skipValidation) {
      return;
    }
    
    // Show loading indicator immediately
    if (this.looksLikeEmail(trimmedInput)) {
      this.isValidatingEmail = true;
      this.isSearching = false;
    } else {
      this.isValidatingEmail = false;
      this.isSearching = true;
    }
    
    // Start timer - wait 2 seconds before making API call (like home component)
    this.typingTimer = setTimeout(() => {
      this.processInput(trimmedInput);
    }, this.DEBOUNCE_TIME);
  }
  
  // Check if input looks like an email address
  private looksLikeEmail(input: string): boolean {
    // Simple heuristic: contains @ and looks like email format
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input);
  }
  
  // Process input after debounce timer - decide whether to validate or search
  private processInput(input: string) {
    if (this.looksLikeEmail(input)) {
      // Input is email - validate it
      console.log('📧 Input looks like email, validating...');
      this.isValidatingEmail = true;
      this.isSearching = false;
      
      this.emailValidationService.validateCorporateEmail(input).subscribe(result => {
        this.emailValidationResult = result;
        this.isValidatingEmail = false;
      });
    } else {
      // Input is name/principal - search for users
      console.log('👤 Input looks like name, searching users...');
      this.isValidatingEmail = false;
      this.isSearching = true;
      
      this.emailValidationService.searchUsers(input).subscribe(results => {
        this.searchResults = results;
        this.showDropdown = results.length > 0;
        this.isSearching = false;
      });
    }
  }
  
  // Select user from dropdown
  selectUser(user: UserSearchResult) {
    // Prefer userPrincipalName (corporate email) over email field
    const emailToUse = user.userPrincipalName || user.email;
    this.newUserEmail = emailToUse;
    this.newUserName = user.displayName;
    this.showDropdown = false;
    this.searchResults = [];
    
    // Validate the selected email
    this.isValidatingEmail = true;
    this.emailValidationService.validateCorporateEmail(emailToUse).subscribe(result => {
      this.emailValidationResult = result;
      this.isValidatingEmail = false;
    });
  }
  
  // Close dropdown when clicking outside
  closeDropdown() {
    this.showDropdown = false;
  }

  // Check if we can add the user (email is valid and exists in AD)
  canAddUser(): boolean {
    const email = this.newUserEmail.trim();
    if (!email) return false;
    
    // If validation is disabled, always allow adding
    if (this.skipValidation) {
      return true;
    }
    
    // Must have validation result and user must exist
    return this.emailValidationResult !== null && 
           this.emailValidationResult.exists === true &&
           this.emailValidationResult.isValid === true;
  }

  addUser() {
    const email = this.newUserEmail.trim();
    
    if (!email) return;
    
    // Check if email is validated and exists in Azure AD
    if (!this.canAddUser()) {
      if (this.emailValidationResult && !this.emailValidationResult.exists) {
        alert('User not found in organization. Please enter a valid corporate email address.');
      } else if (this.emailValidationResult && !this.emailValidationResult.isValid) {
        alert('Please enter a valid email address.');
      } else {
        alert('Please wait for email validation to complete.');
      }
      return;
    }
    
    const existingPerson = this.selectedPeople.find(p => p.email === email);
    
    if (existingPerson) {
      // If person exists but is partial, remove partial state (will apply to all)
      existingPerson.isPartial = false;
      existingPerson.itemCount = this.items.length;
    } else {
      // Add new person (will apply to all items)
      this.selectedPeople.push({
        email: email,
        displayName: this.emailValidationResult?.displayName || email,
        isPartial: false,
        itemCount: this.items.length
      });
      
      // Track as newly added
      this.newlyAddedPeople.add(email);
    }
    
    // Reset input and validation state
    clearTimeout(this.typingTimer);
    this.newUserEmail = '';
    this.newUserName = '';
    this.emailValidationResult = null;
    this.isValidatingEmail = false;
    this.isSearching = false;
    this.searchResults = [];
    this.showDropdown = false;
  }

  removeUser(person: any) {
    // Don't remove if it's the creator
    if (!this.isCreator(person)) {
      // Remove person completely (from all items)
      this.selectedPeople = this.selectedPeople.filter(p => p.email !== person.email);
    }
  }

  startEditing(person: any) {
    person.editing = true;
    person.tempEmail = person.email;
  }

  cancelEdit(person: any) {
    person.editing = false;
    delete person.tempEmail;
  }

  saveEdit(person: any) {
    const email = person.tempEmail?.trim();
    
    if (email && email !== person.email) {
      // Check if new email already exists in other users
      const emailExists = this.selectedPeople.some(p => p !== person && p.email === email);
      if (emailExists) {
        alert('This email is already in the shared list');
        return;
      }
    }
    
    if (email) {
      person.email = email;
      person.editing = false;
      delete person.tempEmail;
    }
  }

  isCreator(person: any): boolean {
    // Check if the person's email matches the creator field
    return this.item?.creator && person.email === this.item.creator;
  }

  // Get initials from email (first 2 letters)
  getOwnerInitials(email: string): string {
    if (!email) return '??';
    const emailLower = email.toLowerCase();
    return emailLower.substring(0, 2).toUpperCase();
  }

  // Generate consistent color for user based on email
  getOwnerColor(email: string): string {
    if (!email) return 'bg-bosch-gray-400';
    
    // Hash the email to get a consistent number
    let hash = 0;
    for (let i = 0; i < email.length; i++) {
      hash = email.charCodeAt(i) + ((hash << 5) - hash);
    }
    
    // Array of pleasant, distinguishable colors
    const colors = [
      'bg-bosch-blue-500',
      'bg-purple-500',
      'bg-green-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-indigo-500',
      'bg-red-500',
      'bg-cyan-500',
      'bg-amber-500',
      'bg-emerald-500',
      'bg-violet-500',
      'bg-fuchsia-500',
      'bg-lime-500',
      'bg-rose-500',
      'bg-sky-500'
    ];
    
    // Use hash to pick a color (same email always gets same color)
    const colorIndex = Math.abs(hash) % colors.length;
    return colors[colorIndex];
  }

  // Check if any changes have been made (people added or removed)
  hasChanges(): boolean {
    // Check if any people were newly added
    if (this.newlyAddedPeople.size > 0) {
      return true;
    }
    
    // Check if any people were removed
    const currentPeopleEmails = new Set(this.selectedPeople.map(p => p.email));
    const hasRemovals = Array.from(this.initialPeopleSnapshot).some(email => !currentPeopleEmails.has(email));
    
    return hasRemovals;
  }

  apply() {
    // Only include people that were NEWLY ADDED during this session (not existing/partial people)
    const newPeopleToAdd = this.selectedPeople
      .filter(p => this.newlyAddedPeople.has(p.email))
      .map(p => ({ email: p.email })); // Clean up, remove metadata
    
    // Detect removed people: in initial snapshot but not in current selectedPeople
    const currentPeopleEmails = new Set(this.selectedPeople.map(p => p.email));
    const removedPeople = Array.from(this.initialPeopleSnapshot).filter(email => !currentPeopleEmails.has(email));
    
    this.shareApplied.emit({
      items: this.items,
      selectedPeople: newPeopleToAdd, // Only newly added people
      removedPeople: removedPeople // List of emails that were removed
    });
    this.close();
  }
}
